/**
 * AtlasHudModule — the diagnostic overlay (UI phase).
 *
 * Reflects connection status, identity, current world, AOI cell, nearby remote
 * count, and placed-object count — enough to eyeball the P0 slice working.
 */

import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { showToast } from '../lib/toast.js';
import { worldToCell, WORLD_KIND } from '../config.js';
import { complexityTier } from '@shared/avatar.js';
import { sfx } from '../lib/sfx.js';

/** Status texts that read as a refusal/failure get the error buzz instead of the notify chime. */
const ERRORISH = /refused|rejected|failed|cannot|can't|no landing|too far|too high|too low|error|denied|insufficient|locked/i;

export class AtlasHudModule implements IModule {
  readonly name = 'AtlasHud';

  private el!: HTMLElement;
  private status = 'connecting…';
  /** Last text written to the DOM — render() touches the element only on a change. */
  private lastText = '';

  init(app: App): void {
    this.el = document.getElementById('hud')!;
    // G3: the HUD, chat, menu bar and prompts leave the picture while a cinematic plays (`body.cinematic`, index.html CSS).
    bus.on('atlas:cinematic', ({ playing }) => { document.body.classList.toggle('cinematic', playing); });
    // THE TOAST (2026-09-23): a reason the player can read where they are looking, gone in 5 s.
    bus.on('atlas:toast', ({ text, kind, ms }) => { showToast(text, kind ?? 'info', ms); });
    bus.on('atlas:status', ({ text }) => {
      // The status bus is the game-wide feedback channel (devices, commerce,
      // crafting, droids, build, flight…) — one hook gives every toast a voice.
      if (text !== this.status) sfx(ERRORISH.test(text) ? 'sfx_error' : 'sfx_notify', { gain: 0.22, cooldownMs: 900 });
      this.status = text;
    });
    app.world.addSystem(makeSystem('AtlasHud', Phase.UI, 50, () => this.render()));
  }

  private render(): void {
    const worldLabel =
      atlas.currentWorldId === null
        ? '—'
        : atlas.personalWorldId !== null && atlas.currentWorldId === atlas.personalWorldId
          ? `personal (#${atlas.currentWorldId})`
          : atlas.commonsWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId
            ? `${WORLD_KIND.Commons} (#${atlas.currentWorldId})`
            : `#${atlas.currentWorldId}`;

    const av = atlas.localAvatar;
    const avatarLine = av
      ? `${av.appearance.bodyTemplate} · complexity ${av.complexity} (${complexityTier(av.complexity)})`
      : '—';

    // Write-on-change: assigning textContent replaces the text node (a DOM
    // mutation + style/layout invalidation) even for an identical string, and
    // this runs every frame — an idle HUD paid that 60×/s.
    const text = [
      'PROJECT COSMOS · P1',
      `status   : ${this.status}`,
      `passport : ${atlas.displayName ?? '—'} (${atlas.username ?? '—'})`,
      `identity : ${atlas.identityHex ? atlas.identityHex.slice(0, 12) + '…' : '—'}`,
      `avatar   : ${avatarLine}`,
      `world    : ${worldLabel}`,
      `cell     : (${worldToCell(atlas.localX)}, ${worldToCell(atlas.localZ)})`,
      `pos      : ${atlas.localX.toFixed(1)}, ${atlas.localZ.toFixed(1)}`,
      `seq      : ${atlas.inputSeq}`,
      `remotes  : ${atlas.remotes.size}`,
      `objects  : ${atlas.worldObjects.size}`,
    ].join('\n');
    if (text === this.lastText) return;
    this.lastText = text;
    this.el.textContent = text;
  }
}
